import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import styles from "./NavMenu.module.css";

export interface NavMenuProps {
  /** The top-level nav label, e.g. "Money". */
  label: string;
  /** Menu contents — links, typically. Any click inside closes the menu. */
  children: ReactNode;
}

/**
 * A top-level nav item that opens a menu of routes beneath it.
 *
 * The panel is portalled to <body> rather than positioned inside the header:
 * the nav strip is a horizontal scroll container (`overflow-x: auto`), which
 * clips a descendant panel in both axes, so an in-flow dropdown would be cut
 * off at the header's edge. Fixed positioning off the trigger's rect avoids
 * that, at the cost of having to close on scroll — which is the behaviour you
 * want from a nav menu anyway.
 *
 * Closes on outside pointer, Escape, scroll, resize, and on any click inside
 * the panel (every item is a navigation).
 */
export function NavMenu({ label, children }: NavMenuProps) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      close();
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        close();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKeyDown);
    // Capture phase: the nav strip and the page both scroll, and neither event
    // bubbles to document from a nested scroller.
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open, close]);

  const toggle = (): void => {
    const next = !open;
    if (next) setRect(triggerRef.current?.getBoundingClientRect() ?? null);
    setOpen(next);
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={toggle}
      >
        {label}
        <span className={styles.caret} aria-hidden="true" />
      </button>
      {open &&
        rect &&
        createPortal(
          <div
            ref={panelRef}
            id={menuId}
            role="menu"
            className={styles.panel}
            style={{ top: rect.bottom + 6, left: rect.left }}
            onClick={close}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}
